import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon, Description as WordIcon, TableChart as ExcelIcon, Email as MsgIcon, InsertDriveFile as GenericFileIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';
import { getFileUrl, getFileName, formatFileSize, isImageFile, isPdfFile, isWordFile, isExcelFile, isMsgFile } from '../../../utils/fileUtils';

/**
 * AttachmentViewDialog Component
 * Dialog for viewing/downloading attachments
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Close handler
 * @param {Object} attachment - Attachment file object
 * @param {function} onDownload - Download handler
 */
const AttachmentViewDialog = ({ open, onClose, attachment, onDownload }) => {
  if (!attachment) return null;

  const fileUrl = getFileUrl(attachment);
  const fileName = getFileName(attachment);
  const fileSize = formatFileSize(attachment.size || attachment.fileSize);
  const isImage = isImageFile(attachment);
  const isPdf = isPdfFile(attachment);
  const isWord = isWordFile(attachment);
  const isExcel = isExcelFile(attachment);
  const isMsg = isMsgFile(attachment);
  const isDocumentType = isWord || isExcel || isMsg;

  /**
   * Get the appropriate icon and color for non-previewable file types
   */
  const getDocumentIcon = () => {
    if (isWord) return { Icon: WordIcon, color: '#2563eb', bgColor: '#dbeafe', label: 'Word Document' };
    if (isExcel) return { Icon: ExcelIcon, color: '#059669', bgColor: '#d1fae5', label: 'Excel Spreadsheet' };
    if (isMsg) return { Icon: MsgIcon, color: '#d97706', bgColor: '#fef3c7', label: 'Outlook Message' };
    return { Icon: GenericFileIcon, color: colors.primary, bgColor: '#e0e7ff', label: 'Document' };
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(attachment);
    } else {
      // Fallback download
      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: borderRadius.dialog,
          boxShadow: '0 24px 48px rgba(14, 23, 42, 0.2)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: spacing.sm,
          borderBottom: `1px solid ${colors.borderDefault}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeMd,
            color: colors.textPrimaryDark,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.backgroundHover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: spacing.sm }}>
        <Box sx={{ mb: spacing.sm }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: fonts.fontSizeSm }}>
            File Size: {fileSize}
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.background,
            borderRadius: borderRadius.input,
            border: `1px solid ${colors.borderDefault}`,
            overflow: 'hidden',
          }}
        >
          {isImage ? (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<Typography>Failed to load image</Typography>';
              }}
            />
          ) : isPdf ? (
            <iframe
              src={fileUrl}
              title={fileName}
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
              }}
            />
          ) : (
            (() => {
              const docInfo = getDocumentIcon();
              const { Icon, color, bgColor, label } = docInfo;
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: spacing.lg,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: spacing.xs,
                    }}
                  >
                    <Icon sx={{ fontSize: '2.5rem', color }} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: fonts.weightSemiBold,
                      color: colors.textPrimaryDark,
                      textAlign: 'center',
                      wordBreak: 'break-word',
                    }}
                  >
                    {fileName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    {label} &middot; {fileSize}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    Preview is not available for this file type. Please download to view.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                      mt: spacing.xs,
                      backgroundColor: colors.primary,
                      color: colors.textWhite,
                      '&:hover': {
                        backgroundColor: colors.primaryHover,
                      },
                    }}
                  >
                    Download {label}
                  </Button>
                </Box>
              );
            })()
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: spacing.sm, borderTop: `1px solid ${colors.borderDefault}` }}>
        <Button onClick={onClose} sx={{ color: colors.textSecondary }}>
          Close
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{
            backgroundColor: colors.primary,
            color: colors.textWhite,
            '&:hover': {
              backgroundColor: colors.primaryHover,
            },
          }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachmentViewDialog;
